import { TrendingUp, AlertTriangle, CheckCircle2, Clock, MinusCircle, ArrowRight } from "lucide-react";

// ─── Data ───────────────────────────────────────────────────────────────────

const STATS = [
  {
    key: "onProgress",
    label: "Berjalan",
    icon: Clock,
    color: "#3b82f6",
    ring: "ring-blue-100",
    bg: "bg-blue-50",
    text: "text-blue-700",
  },
  {
    key: "selesai",
    label: "Selesai",
    icon: CheckCircle2,
    color: "#10b981",
    ring: "ring-emerald-100",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
  },
  {
    key: "terhambat",
    label: "Terhambat",
    icon: AlertTriangle,
    color: "#ef4444",
    ring: "ring-red-100",
    bg: "bg-red-50",
    text: "text-red-700",
  },
  {
    key: "belumMulai",
    label: "Belum Mulai",
    icon: MinusCircle,
    color: "#94a3b8",
    ring: "ring-slate-100",
    bg: "bg-slate-50",
    text: "text-slate-600",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function ProgressKegiatanRekap({ summary, scopedSubs }) {
  const terhambatList = (scopedSubs || [])
    .filter((s) => s.status === "TERHAMBAT")
    .slice(0, 3);

  const pct = Number.isFinite(summary.percent) ? summary.percent : 0;

  return (
    <div
      data-testid="chart-progress-rekap"
      className="rounded-2xl border border-emerald-100/60 p-5 shadow-sm print:shadow-none relative overflow-hidden flex flex-col"
      style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 55%)" }}
    >
      {/* Colored header band */}
      <div
        className="-mx-5 -mt-5 px-5 py-4 mb-4 rounded-t-2xl"
        style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
              <TrendingUp className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-white leading-tight">
                Rekap Progress
              </h3>
              <p className="text-[11px] text-emerald-100 mt-0.5">
                Ringkasan status kegiatan
              </p>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-white bg-white/20 px-2.5 py-1 rounded-full">
            {summary.total} sub
          </span>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
            Capaian Keseluruhan
          </span>
          <span className="text-[14px] font-bold text-emerald-600">{pct}%</span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, #059669, #10b981)",
            }}
          />
        </div>
        <div className="text-[10.5px] text-slate-400 mt-1.5">
          {summary.selesai} dari {summary.total} sub-kegiatan selesai
        </div>
      </div>

      {/* 4-stat grid */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {STATS.map(({ key, label, icon: Icon, color, bg, text }) => (
          <div
            key={key}
            className={`rounded-xl px-3 py-2.5 ${bg} flex items-center gap-2.5`}
          >
            <div
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/70"
            >
              <Icon className="h-3.5 w-3.5" style={{ color }} strokeWidth={2.2} />
            </div>
            <div>
              <div className={`text-[17px] font-bold leading-none ${text}`}>
                {summary[key] ?? 0}
              </div>
              <div className="text-[9.5px] text-slate-500 mt-0.5 leading-tight">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Perlu Perhatian / Status aman */}
      <div className="flex-1">
        {terhambatList.length > 0 ? (
          <div className="rounded-xl border border-red-100 bg-red-50/70 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" strokeWidth={2} />
              <span className="text-[11px] font-bold text-red-600">
                Butuh Perhatian ({summary.terhambat})
              </span>
            </div>
            <div className="space-y-1.5">
              {terhambatList.map((s) => (
                <div key={s.id} className="flex items-start gap-1.5">
                  <span className="font-mono text-[9px] text-slate-400 flex-shrink-0 mt-[1px] bg-slate-100 px-1 rounded">
                    {s.programKode}
                  </span>
                  <span className="text-[10.5px] text-slate-700 leading-snug line-clamp-1">
                    {s.nama}
                  </span>
                </div>
              ))}
              {summary.terhambat > 3 && (
                <div className="text-[10px] text-red-500 font-semibold pl-0.5">
                  +{summary.terhambat - 3} kegiatan lainnya terhambat
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-3 flex items-center gap-2.5">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" strokeWidth={2} />
            <div>
              <div className="text-[12px] font-bold text-emerald-700">Semua berjalan lancar</div>
              <div className="text-[10.5px] text-emerald-600">Tidak ada kegiatan terhambat</div>
            </div>
          </div>
        )}
      </div>

      {/* CTA to Progress page */}
      <a
        href="/progress"
        className="mt-4 flex items-center justify-center gap-1.5 rounded-xl py-2 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
        style={{ background: "linear-gradient(135deg, #059669, #10b981)" }}
      >
        Lihat Detail Progress
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}
